import { useState, useMemo } from "react";
import CardCharts from "../../components/common/CardCharts/CardCharts";
import CardStats from "../../components/common/CardStats/CardStats";
import ProgressCircle from "../../components/common/ProgressCircle/ProgressCircle";
import SimpleLineChartGraph from "../../components/common/Charts/SimpleLineChartGraph";
import BarChartGraph from "../../components/common/Charts/BarChartGraph";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import InputDropdown from "../../components/inputs/InputDropdown/InputDropdown";
import {
    barChartData,
    lineChartData,
    dashboardStatsData,
    indicadoresData,
} from "./data/data";
import "./Dashboard.scss";
import MapComponent from "../../components/common/MapComponent/MapComponent";
import { useEffect } from "react";
import { getApiInstance } from "../../utils/api";
import { useZone } from "../../context/ZoneContext";
import { formatDate } from "../../utils/utils";
import { FiPlay, FiUsers } from "react-icons/fi";
import { IoStopOutline } from "react-icons/io5";
import { LuWeight } from "react-icons/lu";
import { MdEuroSymbol } from "react-icons/md";
import ProgressBar from "../../components/common/ProgressBar/ProgressBar";
import Badge from "../../components/common/Badge/Badge";
import Button from "../../components/common/Button/Button";
import { CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { CiCircleCheck } from "react-icons/ci";
import { CiCircleInfo } from "react-icons/ci";
import { CiWarning } from "react-icons/ci";

const cumplimientoStates = [
    { label: "Sin datos", type: "info", Icon: CiClock1 },
    { label: "Pendiente", type: "info", Icon: CiClock1 },
    { label: "Validado con incidencias", type: "warning", Icon: CgDanger },
    { label: "Validada", type: "success", Icon: CiCircleCheck },
];

const periodOptions = [
    { value: "Semana", label: "Semana" },
    { value: "Mes", label: "Mes" },
    { value: "Año", label: "Año" },
];

export default function Dashboard() {
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedZone } = useZone();
    const [mapCenter, setMapCenter] = useState([40.0, -4.0]);
    const [zoom, setZoom] = useState(5);
    const [selectedPeriod, setSelectedPeriod] = useState("Semana");

    const fetchObras = async () => {
        setLoading(true);
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Obra/GetObrasFromZona/${selectedZone.value}/1/es`
            );

            setObras(response.data);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las obras. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchObras();

        if (selectedZone.value == 2) {
            setMapCenter([24.056145870396488, -102.51185975545415]);
            setZoom(5);
        } else if (selectedZone.value == 3) {
            setMapCenter([32.679109313985286, -28.420501580530633]);
            setZoom(1);
        } else {
            setMapCenter([40.0, -4.0]);
            setZoom(5);
        }
    }, [selectedZone]);

    const getValorizacionStatus = (centro) => {
        // Si viene el porcentaje real de la API, usarlo
        if (
            centro.porcentajeValorizacion !== undefined &&
            centro.porcentajeValorizacion !== null
        ) {
            return Math.max(0, Math.min(100, centro.porcentajeValorizacion));
        }

        // Generar porcentaje único basado en el ID del centro
        const id = centro.codObra || centro.id || centro.codEspacio || "0";
        let hash = 0;
        const str = id.toString();

        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32bit integer
        }

        // Convertir hash a porcentaje entre 0-100
        const percentage = Math.abs(hash) % 101;
        return percentage;
    };

    const getCumplimientoStatus = (centro) => {
        switch (centro.codEstado) {
            case "COMPLETADO":
            case "VALIDADO":
                return {
                    status: "CORRECTO",
                    variant: "success",
                    Icon: CiCircleCheck,
                };
            case "INCIDENCIA":
            case "RETRASADO":
                return {
                    status: "INCORRECTO",
                    variant: "danger",
                    Icon: CgDanger,
                };
            case "PENDIENTE":
            case "EN_PROGRESO":
                return {
                    status: "PENDIENTE",
                    variant: "warning",
                    Icon: CiWarning,
                };
            default:
                return {
                    status: "SIN DATOS",
                    variant: "outlined",
                    Icon: CiCircleInfo,
                };
        }
    };

    const modalMap = (centro) => {
        return (
            <div className="centro-map-details">
                <div className="centro-header">
                    <h3 className="centro-title">
                        {centro.nombre || centro.codEspacio || ""},{" "}
                        {centro.direccion || ""}
                    </h3>
                </div>

                <div className="centro-info-section">
                    <div className="info-row">
                        <FiPlay className="info-icon" />
                        <span className="info-label">Fecha inicio</span>
                        <span className="info-value">
                            {formatDate(centro.fechaInicio) || ""}
                        </span>
                    </div>
                    <div className="info-row">
                        <IoStopOutline className="info-icon" />
                        <span className="info-label">Fecha fin</span>
                        <span className="info-value">
                            {formatDate(centro.fechaFin) || ""}
                        </span>
                    </div>

                    <div className="info-row info-row--highlight">
                        <FiUsers className="info-icon" />
                        <span className="info-label">Intermediario</span>
                        <span className="info-value intermediario-link">
                            {centro.intermediario || ""}
                        </span>
                    </div>

                    <div className="info-row">
                        <LuWeight className="info-icon" />
                        <span className="info-label">Cantidades generadas</span>
                        <span className="info-value">{centro.peso || ""}</span>
                    </div>

                    <div className="info-row">
                        <MdEuroSymbol className="info-icon" />
                        <span className="info-label">Coste</span>
                        <span className="info-value">
                            {centro.costeTotal || ""}
                        </span>
                    </div>
                </div>

                <div className="centro-valorizacion-section">
                    <h4 className="section-title">Valorización</h4>
                    <div className="valorizacion-percentage">
                        {(centro.peso
                            ? (centro.valorizacion / centro.peso) * 100
                            : 0
                        ).toFixed(2)}
                        %
                    </div>
                    <ProgressBar
                        percentage={
                            (centro.peso
                                ? (centro.valorizacion / centro.peso) * 100
                                : 0
                            ).toFixed(2) || 0
                        }
                        height={8}
                        width="100%"
                    />
                </div>

                <div className="centro-cumplimiento-section">
                    <div className="cumplimiento-header">
                        <h4 className="section-title">Cumplimiento Legal</h4>
                        <Badge
                            variant={getCumplimientoStatus(centro).variant}
                            Icon={getCumplimientoStatus(centro).Icon}
                        >
                            {getCumplimientoStatus(centro).status}
                        </Badge>
                        {centro.cumplimientos?.map((cumplimiento, index) => (
                            <div key={index} className="cumplimiento-item">
                                <span className="cumplimiento-name">
                                    {cumplimiento.nombre || ""}
                                </span>
                                <Badge
                                    variant={
                                        cumplimientoStates[centro.codEstado]
                                            ?.variant
                                    }
                                    Icon={
                                        cumplimientoStates[centro.codEstado]
                                            ?.Icon
                                    }
                                >
                                    {
                                        cumplimientoStates[centro.codEstado]
                                            ?.status
                                    }
                                </Badge>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-actions">
                    <Button
                        variant="filled"
                        onClick={() => {
                            window.location.href = `/centros-productores/${centro.codObra}`;
                        }}
                    >
                        Ver Ficha Completa
                    </Button>
                </div>
            </div>
        );
    };

    const currentPeriodOption = periodOptions.find(
        (opt) => opt.value === selectedPeriod
    );

    function buildDashboardData(obras, now = new Date()) {
        // ----------------- Helpers -----------------
        const fmtNumber = (n, decimals = 0) =>
            n.toLocaleString("es-ES", {
                maximumFractionDigits: decimals,
                minimumFractionDigits: decimals,
            });

        const fmtTn = (tn, decimals = 3) => `${fmtNumber(tn, decimals)} tn`;
        const kgToTn = (kg) => kg / 1000;

        const periodRange = (key, date = new Date()) => {
            const start = new Date(date);
            const end = new Date(date);

            if (key === "Semana") {
                // Lunes a domingo de la semana actual
                const day = (date.getDay() + 6) % 7; // 0 = lunes
                start.setDate(date.getDate() - day);
                start.setHours(0, 0, 0, 0);
                end.setDate(start.getDate() + 6);
                end.setHours(23, 59, 59, 999);
            } else if (key === "Mes") {
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(start.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
            } else {
                start.setMonth(0, 1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(11, 31);
                end.setHours(23, 59, 59, 999);
            }
            return { start, end };
        };

        const previousPeriodRange = (key, date = new Date()) => {
            const { start } = periodRange(key, date);
            const prevEnd = new Date(start.getTime() - 1);
            if (key === "Semana") {
                const prevStart = new Date(prevEnd);
                prevStart.setDate(prevEnd.getDate() - 6);
                prevStart.setHours(0, 0, 0, 0);
                prevEnd.setHours(23, 59, 59, 999);
                return { start: prevStart, end: prevEnd };
            } else if (key === "Mes") {
                const prevStart = new Date(start);
                prevStart.setMonth(start.getMonth() - 1, 1);
                prevStart.setHours(0, 0, 0, 0);
                const prevEnd2 = new Date(prevStart);
                prevEnd2.setMonth(prevStart.getMonth() + 1, 0);
                prevEnd2.setHours(23, 59, 59, 999);
                return { start: prevStart, end: prevEnd2 };
            } else {
                const prevStart = new Date(start);
                prevStart.setFullYear(start.getFullYear() - 1, 0, 1);
                prevStart.setHours(0, 0, 0, 0);
                const prevEnd2 = new Date(prevStart);
                prevEnd2.setMonth(11, 31);
                prevEnd2.setHours(23, 59, 59, 999);
                return { start: prevStart, end: prevEnd2 };
            }
        };

        const inRange = (d, start, end) => d >= start && d <= end;

        // Reglas de "activo": ajusta si tienes catálogo de estados cerrados.
        const isActive = (obra) => {
            const estadosNoActivos = new Set([]); // e.g., new Set([9, 10])
            if (obra.codEstado != null && estadosNoActivos.has(obra.codEstado))
                return false;
            if (obra.fechaFin) {
                const fin = new Date(obra.fechaFin);
                if (!Number.isNaN(+fin) && fin < new Date()) return false;
            }
            return true;
        };

        // ---- Total Centros ----
        const computeTotalCentros = (obras) => {
            const activos = obras.filter(isActive).length;
            const metric = {
                amount: String(activos),
                percentageValue: 0,
                percentageText: "sin variación",
            };
            return {
                title: "Total Centros",
                subtitle: "Activos en la red",
                metrics: {
                    Semana: { ...metric },
                    Mes: { ...metric },
                    Año: { ...metric },
                },
            };
        };

        // ---- Cantidad generada (tn) ----
        const sumResiduosInPeriodTn = (obras, start, end) => {
            let totalTn = 0;
            for (const obra of obras) {
                const residuos = Array.isArray(obra.residuos)
                    ? obra.residuos
                    : [];
                for (const r of residuos) {
                    // Si hay fecha, filtra por periodo; si no, puedes incluirlos siempre (aquí: solo si pasan el filtro o no tienen fecha)
                    if (r.fecha) {
                        const d = new Date(r.fecha);
                        if (Number.isNaN(+d) || !inRange(d, start, end))
                            continue;
                    }
                    const qty = typeof r.cantidad === "number" ? r.cantidad : 0;
                    const tn = r.unidad === "kg" ? kgToTn(qty) : qty;
                    totalTn += tn;
                }
            }
            return totalTn;
        };

        const computeCantidadGenerada = (obras, now) => {
            const build = (key) => {
                const { start, end } = periodRange(key, now);
                const { start: ps, end: pe } = previousPeriodRange(key, now);

                const actual = sumResiduosInPeriodTn(obras, start, end);
                const previo = sumResiduosInPeriodTn(obras, ps, pe);
                const pct = previo > 0 ? ((actual - previo) / previo) * 100 : 0;

                return {
                    amount: fmtTn(actual),
                    percentageValue: Number(pct.toFixed(2)),
                    percentageText:
                        previo > 0
                            ? `vs ${key.toLowerCase()} anterior`
                            : "sin variación",
                };
            };

            return {
                title: "Cantidad generada",
                subtitle: "Total procesado",
                metrics: {
                    Semana: build("Semana"),
                    Mes: build("Mes"),
                    Año: build("Año"),
                },
            };
        };

        // ---- Eficiencia global ----
        const sumValorizacionEliminacionInPeriod = (obras, start, end) => {
            let valorizado = 0;
            let eliminado = 0;

            for (const obra of obras) {
                const residuos = Array.isArray(obra.residuos)
                    ? obra.residuos
                    : [];
                let hasResidueLevel = false;

                for (const r of residuos) {
                    if (
                        r.fecha &&
                        (typeof r.valorizado === "number" ||
                            typeof r.eliminado === "number")
                    ) {
                        const d = new Date(r.fecha);
                        if (Number.isNaN(+d) || !inRange(d, start, end))
                            continue;
                        valorizado +=
                            typeof r.valorizado === "number" ? r.valorizado : 0;
                        eliminado +=
                            typeof r.eliminado === "number" ? r.eliminado : 0;
                        hasResidueLevel = true;
                    }
                }

                // Si no hay detalle con fecha, usa totales de obra (sin rango)
                if (!hasResidueLevel) {
                    valorizado +=
                        typeof obra.valorizacion === "number"
                            ? obra.valorizacion
                            : 0;
                    eliminado +=
                        typeof obra.eliminacion === "number"
                            ? obra.eliminacion
                            : 0;
                }
            }

            return { valorizado, eliminado };
        };

        const computeEficienciaGlobal = (obras, now) => {
            const build = (key) => {
                const { start, end } = periodRange(key, now);
                const { start: ps, end: pe } = previousPeriodRange(key, now);

                const { valorizado, eliminado } =
                    sumValorizacionEliminacionInPeriod(obras, start, end);
                const { valorizado: vPrev, eliminado: ePrev } =
                    sumValorizacionEliminacionInPeriod(obras, ps, pe);

                const denom = valorizado + eliminado;
                const denomPrev = vPrev + ePrev;

                const eff = denom > 0 ? valorizado / denom : 0;
                const effPrev = denomPrev > 0 ? vPrev / denomPrev : null;

                const pctDelta = effPrev != null ? (eff - effPrev) * 100 : 0;

                // Formateo de porcentaje "72,21%"
                const amount =
                    (eff * 100).toLocaleString("es-ES", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    }) + "%";

                return {
                    amount,
                    percentageValue: Number(pctDelta.toFixed(2)),
                    percentageText:
                        effPrev != null ? "mejora" : "sin variación",
                };
            };

            return {
                title: "Eficiencia global",
                subtitle: "Valorización promedio",
                metrics: {
                    Semana: build("Semana"),
                    Mes: build("Mes"),
                    Año: build("Año"),
                },
            };
        };

        // ----------------- Compose -----------------
        return {
            totalCentros: computeTotalCentros(obras),
            cantidadGenerada: computeCantidadGenerada(obras, now),
            eficienciaGlobal: computeEficienciaGlobal(obras, now),
        };
    }

    const dashboardStatsData = useMemo(() => {
        try {
            if (!Array.isArray(obras)) return null;
            return buildDashboardData(obras, new Date());
        } catch {
            return null;
        }
    }, [obras]);

    const emptyMetrics = {
        Semana: { amount: "-", percentageValue: 0, percentageText: "" },
        Mes: { amount: "-", percentageValue: 0, percentageText: "" },
        Año: { amount: "-", percentageValue: 0, percentageText: "" },
    };

    const totalCentrosMetrics =
        dashboardStatsData?.totalCentros?.metrics ?? emptyMetrics;

    const cantidadGenerada = {
        title:
            dashboardStatsData?.cantidadGenerada?.title ?? "Cantidad generada",
        subtitle:
            dashboardStatsData?.cantidadGenerada?.subtitle ?? "Total procesado",
        metrics: dashboardStatsData?.cantidadGenerada?.metrics ?? emptyMetrics,
    };

    const eficienciaGlobal = {
        title:
            dashboardStatsData?.eficienciaGlobal?.title ?? "Eficiencia global",
        subtitle:
            dashboardStatsData?.eficienciaGlobal?.subtitle ??
            "Valorización promedio",
        metrics: dashboardStatsData?.eficienciaGlobal?.metrics ?? emptyMetrics,
    };

    return (
        <PageLayout
            title="Resumen de tu actividad"
            subtitle="Aquí visualizarás un resumen de los datos más importantes de tu organización"
            // actions={[
            //     <InputDropdown
            //         key="period-selector"
            //         value={currentPeriodOption}
            //         options={periodOptions}
            //         onChange={(option) => setSelectedPeriod(option.value)}
            //         placeholder="Seleccionar período"
            //         labelVisible={false}
            //     />,
            // ]}
        >
            <div className="dashboard__hero">
                <CardStats
                    title="Total centros"
                    // subtitle="Activos en la red"
                    data={{ metrics: totalCentrosMetrics }}
                />

                <CardStats
                    title={cantidadGenerada.title}
                    subtitle={cantidadGenerada.subtitle}
                    data={{ metrics: cantidadGenerada.metrics }}
                />

                <CardStats
                    title={eficienciaGlobal.title}
                    subtitle={eficienciaGlobal.subtitle}
                    data={{ metrics: eficienciaGlobal.metrics }}
                />
            </div>

            {/* <div className="dashboard__hero">
                <CardStats
                    title={dashboardStatsData.totalCentros.title}
                    subtitle={dashboardStatsData.totalCentros.subtitle}
                    data={{ metrics: dashboardStatsData.totalCentros.metrics }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />

                <CardStats
                    title={dashboardStatsData.cantidadGenerada.title}
                    subtitle={dashboardStatsData.cantidadGenerada.subtitle}
                    data={{
                        metrics: dashboardStatsData.cantidadGenerada.metrics,
                    }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />

                <CardStats
                    title={dashboardStatsData.eficienciaGlobal.title}
                    subtitle={dashboardStatsData.eficienciaGlobal.subtitle}
                    data={{
                        metrics: dashboardStatsData.eficienciaGlobal.metrics,
                    }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />

                <CardStats
                    title="Coste Total"
                    subtitle="Gastos en gestión"
                    data={{
                        metrics: {
                            Semana: {
                                amount: "45.230€",
                                percentageValue: 8.2,
                                trendDirection: "up",
                            },
                            Mes: {
                                amount: "182.450€",
                                percentageValue: 12.5,
                                trendDirection: "up",
                            },
                            Año: {
                                amount: "2.145.600€",
                                percentageValue: 5.3,
                                trendDirection: "up",
                            },
                        },
                    }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />
            </div>

            <div className="dashboard__indicators">
                <CardCharts
                    title="Indicadores clave"
                    subtitle="Métricas de rendimiento"
                    showTabs={false}
                >
                    <div className="dashboard__circles-grid">
                        {indicadoresData[selectedPeriod]?.map(
                            (indicador, index) => (
                                <ProgressCircle
                                    key={index}
                                    percentage={indicador.percentage}
                                    label={indicador.label}
                                    value={indicador.value}
                                    className="progress-circle--large"
                                />
                            )
                        )}
                    </div>
                </CardCharts>
            </div>

            <div className="dashboard__charts">
                <CardCharts
                    title="Evolución temporal"
                    subtitle="Tendencia de generación"
                    data={lineChartData}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                >
                    <SimpleLineChartGraph data={lineChartData} />
                </CardCharts>

                <CardCharts
                    title="Distribución por tipo"
                    subtitle="Categorías de residuos"
                    data={barChartData}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                >
                    <BarChartGraph data={barChartData} />
                </CardCharts>
            </div> */}

            <div className="dashboard__bottom">
                <div style={{ width: "100%" }}>
                    <MapComponent
                        data={obras}
                        center={mapCenter}
                        zoom={zoom}
                        height="600px"
                        width="100%"
                        modalContent={modalMap}
                    />
                </div>
            </div>
        </PageLayout>
    );
}
